import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
    SelectedFilterMultiValueType,
    SelectedFilterValueType,
    SelectedSearchValueType,
} from './ai-voice-modal-types';

import type { AIVoice } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';

export type AIVoiceFilters = {
    page: number | null;
    search: SelectedSearchValueType;
    languages: SelectedFilterMultiValueType;
    style: SelectedFilterValueType;
    accent: SelectedFilterValueType;
    tone: SelectedFilterValueType;
    age: SelectedFilterValueType;
    gender: SelectedFilterValueType;
    quality: SelectedFilterValueType;
    favourites: boolean;
};

const getInitialFiltersState = (): AIVoiceFilters => ({
    page: null,
    search: null,
    languages: [],
    style: null,
    accent: null,
    tone: null,
    age: null,
    gender: null,
    quality: null,
    favourites: false,
});

export type AIVoicesQueryStatus = 'uninitialized' | 'pending' | 'fulfilled' | 'rejected';

export type AIVoicesRequestStatus = {
    status: AIVoicesQueryStatus;
    requestId: string | null;
    error: string | null;
};

const getInitialRequestState = (): AIVoicesRequestStatus => ({
    status: 'uninitialized',
    requestId: null,
    error: null,
});

const getInitialVoicesState = () => PlainJsStoreORM.createEmpty<AIVoice, string>().data;

const getInitialDataState = () => ({
    voices: getInitialVoicesState(),
    filters: getInitialFiltersState(),
});

const getInitialState = () => ({
    data: getInitialDataState(),
    request: getInitialRequestState(),
});

export const aiVoiceModalSlice = createSlice({
    name: 'aiVoiceModal',
    initialState: getInitialState(),
    reducers: {
        createAIVoiceSearch: (state, action: PayloadAction<Partial<AIVoiceFilters>>) => {
            state.data.filters = {
                ...state.data.filters,
                ...action.payload,
                page: null,
            };
            state.data.voices = getInitialVoicesState();
        },
        clearAIVoiceSearch: (state) => {
            const updatedFilters = getInitialFiltersState();
            updatedFilters.search = state.data.filters.search;
            state.data.filters = updatedFilters;
            state.data.voices = getInitialVoicesState();
        },
        setAIVoicesRequestStarted: (state, action: PayloadAction<string>) => {
            state.request.status = 'pending';
            state.request.requestId = action.payload;
            state.request.error = null;
        },
        setAIVoicesRequestSucceeded: (
            state,
            action: PayloadAction<{
                requestId: string;
                voices: AIVoice[];
                nextPage: number | null;
            }>,
        ) => {
            if (state.request.requestId !== action.payload.requestId) {
                return;
            }
            const orm = new PlainJsStoreORM(state.data.voices);
            action.payload.voices.forEach((voice) => {
                orm.pushItem(voice, 'slug');
            });
            state.data.voices = orm.data;
            state.data.filters.page = action.payload.nextPage;
            state.request.status = 'fulfilled';
            state.request.error = null;
        },
        setAIVoicesRequestFailed: (
            state,
            action: PayloadAction<{ requestId: string; error: string }>,
        ) => {
            if (state.request.requestId !== action.payload.requestId) {
                return;
            }
            state.request.status = 'rejected';
            state.request.error = action.payload.error;
        },
        resetAIVoiceModal: (state) => {
            state.data = getInitialDataState();
            state.request = getInitialRequestState();
        },
        toggleVoiceFavourite: (state, action: PayloadAction<string>) => {
            const orm = new PlainJsStoreORM(state.data.voices);
            const voice = orm.getById(action.payload);
            if (voice) {
                orm.pushItem({ ...voice, is_favourited: !voice.is_favourited }, 'slug');
                state.data.voices = orm.data;
            }
        },
    },
});

export const {
    createAIVoiceSearch,
    clearAIVoiceSearch,
    setAIVoicesRequestStarted,
    setAIVoicesRequestSucceeded,
    setAIVoicesRequestFailed,
    resetAIVoiceModal,
    toggleVoiceFavourite,
} = aiVoiceModalSlice.actions;
